import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Loader2, ScanFace, Camera, Crown, ArrowRight, AlertTriangle } from 'lucide-react';
import FaceScanModal from '../components/FaceScanModal';
import SkinReportCard from '../components/SkinReportCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScanPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [phase, setPhase] = useState('verifying'); // verifying | ready | report | failed
  const [report, setReport] = useState(null);
  const [scanImage, setScanImage] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const pollRef = useRef(0);

  const checkStatus = useCallback(async () => {
    if (!sessionId) { setPhase('failed'); return; }
    try {
      const { data } = await axios.get(`${API}/scan/checkout/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        if (data.analysis) {
          setReport({ analysis: data.analysis, created_at: data.analyzed_at });
          setPhase('report');
        } else {
          setPhase('ready');
        }
        return;
      }
      if (data.status === 'expired') { setPhase('failed'); return; }
      // keep polling for up to ~25s
      pollRef.current += 1;
      if (pollRef.current > 12) { setPhase('failed'); return; }
      setTimeout(checkStatus, 2000);
    } catch (e) {
      pollRef.current += 1;
      if (pollRef.current > 12) { setPhase('failed'); return; }
      setTimeout(checkStatus, 2000);
    }
  }, [sessionId]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const handleCapture = useCallback(async (base64) => {
    const { data } = await axios.post(`${API}/scan/analyze`, { session_id: sessionId, image_base64: base64 });
    setScanImage(`data:image/jpeg;base64,${base64}`);
    setReport(data);
    setPhase('report');
    toast.success('Your AI skin report is ready!');
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFF2EC]" data-testid="scan-page">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 mb-8">
          <ScanFace className="w-6 h-6 text-[#FF6B4A]" />
          <span className="font-heading text-xl font-bold text-[#1A1514]">InnerGlow · AI Skin Analysis</span>
        </div>

        {phase === 'verifying' && (
          <div className="text-center py-20" data-testid="scan-verifying">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#FF6B4A] mb-4" />
            <p className="font-semibold text-[#1A1514]">Confirming your payment…</p>
            <p className="text-sm text-[#8C7A76] mt-1">This only takes a moment.</p>
          </div>
        )}

        {phase === 'ready' && (
          <div className="text-center py-16 bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] rounded-2xl text-white px-6" data-testid="scan-ready">
            <div className="w-16 h-16 rounded-full bg-[#4ADE80]/20 flex items-center justify-center mx-auto mb-4">
              <ScanFace className="w-8 h-8 text-[#4ADE80]" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Payment confirmed — let's scan!</h2>
            <p className="text-sm text-white/75 mb-6 max-w-sm mx-auto">Position your face, ensure good lighting, and capture for your full clinical skin report.</p>
            <Button onClick={() => setScanOpen(true)} className="bg-[#4ADE80] hover:bg-[#94a37b] text-[#161b17] font-bold rounded-full px-8 py-6 text-base" data-testid="scan-start-btn">
              <Camera className="mr-2 w-5 h-5" /> Scan My Skin
            </Button>
          </div>
        )}

        {phase === 'report' && report && (
          <div className="space-y-6" data-testid="scan-report">
            <SkinReportCard selfie={report} imageSrc={scanImage} onUpgrade={() => navigate('/')} />
            <div className="rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] text-white p-6 text-center">
              <Crown className="w-7 h-7 mx-auto text-[#4ADE80] mb-2" />
              <h3 className="font-heading text-lg font-bold mb-1">Want to track your skin over time?</h3>
              <p className="text-sm text-white/75 mb-4 max-w-md mx-auto">Create a free account to log daily, see before/after progress, and unlock unlimited scans with Premium.</p>
              <Button onClick={() => navigate('/')} className="bg-white text-[#1A1514] hover:bg-white/90 rounded-full px-7 py-5 font-semibold" data-testid="scan-signup-cta">
                Create free account <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {phase === 'failed' && (
          <div className="text-center py-20" data-testid="scan-failed">
            <AlertTriangle className="w-10 h-10 mx-auto text-[#A388EE] mb-4" />
            <p className="font-semibold text-[#1A1514]">We couldn't confirm your payment</p>
            <p className="text-sm text-[#8C7A76] mt-1 mb-6">If you were charged, please refresh in a moment. Otherwise, try again.</p>
            <Button onClick={() => navigate('/')} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full px-6" data-testid="scan-home-btn">Back to home</Button>
          </div>
        )}
      </div>

      <FaceScanModal open={scanOpen} onOpenChange={setScanOpen} onCapture={handleCapture} />
    </div>
  );
};

export default ScanPage;
